import type { Blocker, Part } from "@/components/ui/box.schema";
import { missingFields, unreadColumns, type ColumnMap, type SourceField, type SourceTable } from "@/lib/source-columns";
import { columnsText, columnsWords, MISSING } from "./source-columns-copy";
import type { FormatLine } from "./SourceFormat";
import type { ImportSource } from "./useImportTask";

/** A mapped export's fields as format lines: what each reads, whether it is needed, and the headers that suggest it. */
export function fieldLines(fields: readonly SourceField[], notes: Record<string, string>): FormatLine[] {
  return fields.map((f) => ({ column: f.label, readsWhen: f.required ? columnsWords.required : columnsWords.optional, note: `${notes[f.id]}${columnsText.alsoReads(f.aliases)}` }));
}

/** The column map a read export carries in its source; null for the template and typed rows. */
export function columnMapOf(source: ImportSource | null): ColumnMap | null {
  if (!source?.review) return null;
  return Object.fromEntries(Object.entries(source.review).filter((entry): entry is [string, number] => typeof entry[1] === "number"));
}

/** A required field without a column holds the import, by name. */
export function columnsBlockers(part: string, source: ImportSource | null, fields: readonly SourceField[]): Blocker[] {
  const map = columnMapOf(source);
  return map ? missingFields(fields, map).map((f) => ({ kind: "missing" as const, part, sentence: MISSING[f.id as keyof typeof MISSING] ?? columnsText.chooseColumn(f) })) : [];
}

/** The Columns part: every field's column, visible and changeable, and how the rows are derived from them. */
export function columnsPart({ id, source, table, fields, derivation, onMap }: {
  id: string; source: ImportSource | null; table: SourceTable | null; fields: readonly SourceField[];
  derivation: readonly string[]; onMap: (map: ColumnMap) => void;
}): Part {
  const map = columnMapOf(source);
  const waiting = map !== null && missingFields(fields, map).length > 0;
  return { id, label: columnsWords.columns, owner: { system: "imam" }, fold: "choice", draft: "browser",
    // Never quiet: a part that starts quiet starts folded, and a read export must show its columns.
    state: !source ? { class: "draft", word: columnsWords.noFileRead } : !map ? { class: "draft", word: columnsWords.readAsWritten }
      : { class: waiting ? "waiting" : "draft", word: waiting ? columnsWords.columnNeeded : columnsWords.columnsChosen },
    render: () => !source ? <p>{columnsWords.readFirst}</p> : !map ? <p>{columnsWords.asWritten}</p>
      : columnsContent({ id, map, table, fields, derivation, onMap }) };
}

/** The part's content, drawn by its render: every field's column and the derivation it feeds. */
function columnsContent({ id, map, table, fields, derivation, onMap }: { id: string; map: ColumnMap; table: SourceTable | null; fields: readonly SourceField[];
  derivation: readonly string[]; onMap: (map: ColumnMap) => void }) {
  const choose = (field: string, value: string) => {
    const next = { ...map };
    if (value === "") delete next[field]; else next[field] = Number(value);
    onMap(next);
  };
  const unread = table ? unreadColumns(table.header, map) : [];
  return <div className="grid gap-4 text-ui">
    <p>{columnsWords.intro}</p>
    <section className="grid gap-1" aria-label={columnsWords.howRowsAreRead}>
      <h4 className="font-medium">{columnsWords.howRowsAreRead}</h4>
      <ul className="grid gap-1 list-disc pl-5">{derivation.map((line) => <li key={line}>{line}</li>)}</ul>
    </section>
    {!table ? <p role="status">{columnsWords.readAgain}</p> : <div className="grid gap-3">
      {fields.map((f) => {
        const control = `${id}-${f.id}`;
        const column = map[f.id];
        const sample = table && column !== undefined ? (table.records[0]?.[column] ?? "").trim() : "";
        return <div key={f.id} className="grid gap-1 border-t border-rule pt-2">
          <label htmlFor={control} className="font-medium">{f.label}</label>
          <span className="text-meta text-muted-foreground">{f.required ? columnsWords.required : columnsWords.optional}{sample ? ` · ${columnsText.firstLine(sample)}` : ""}</span>
          <select id={control} value={column ?? ""} onChange={(e) => choose(f.id, e.target.value)}>
            <option value="">{columnsWords.notInFile}</option>
            {table.header.map((name, index) => <option key={index} value={index}>{name.trim() || `Column ${index + 1}`}</option>)}
          </select>
        </div>;
      })}
    </div>}
    {unread.length > 0 && <p className="text-muted-foreground">{columnsText.unread(unread)}</p>}
  </div>;
}
